(() => {
  const CONTEXT_STORAGE_KEY = 'cc_creator_context_v1';
  const CART_SYNC_HASH_KEY = 'cc_creator_cart_sync_hash_v1';
  const CART_SYNC_TIMEOUT_MS = 5000;

  const parseList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    }

    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const parseJSON = (value) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const parsePositiveInteger = (value) => {
    const parsed = Number.parseInt(String(value || '').trim(), 10);
    if (!Number.isInteger(parsed) || parsed <= 0) return null;
    return parsed;
  };

  const parseProductList = (value) => {
    if (!value) return [];

    const normalizeProduct = (product) => {
      if (!product || typeof product !== 'object') return null;

      const handle = String(product.handle || product.product_handle || '').trim().toLowerCase();
      const title = String(product.title || product.product_title || '').trim();
      const type = String(product.type || product.product_type || '').trim();
      const shopifyId = String(product.shopify_id || product.product_id || product.id || '').trim();

      if (!handle && !title && !type && !shopifyId) return null;

      return {
        handle,
        title,
        type,
        shopify_id: shopifyId
      };
    };

    if (Array.isArray(value)) {
      return value.map(normalizeProduct).filter(Boolean);
    }

    if (typeof value === 'string') {
      const raw = value.trim();
      if (!raw) return [];

      const parsed = parseJSON(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(normalizeProduct).filter(Boolean);
      }

      return parseList(raw).map((handle) => ({
        handle: handle.toLowerCase(),
        title: '',
        type: '',
        shopify_id: ''
      }));
    }

    return [];
  };

  const readStoredContext = () => parseJSON(window.localStorage.getItem(CONTEXT_STORAGE_KEY)) || {};

  const readMetaobjectContext = () => (window.CC_CREATOR_METAOBJECT && typeof window.CC_CREATOR_METAOBJECT === 'object'
    ? window.CC_CREATOR_METAOBJECT
    : {});

  const readQueryContext = () => {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    return {
      handle: params.get('cc_handle') || '',
      discount_code: params.get('cc_discount_code') || '',
      discount_amount: params.get('cc_discount_pct') || '',
      template_type: params.get('cc_template_type') || '',
      default_build_box_size: params.get('size') || '',
      most_recent_drop_products: parseProductList(params.get('cc_drop_products')),
      most_recent_drop_handles: parseList(params.get('cc_drop_handles')),
      featured_handles: parseList(params.get('cc_featured_handles'))
    };
  };

  const hasExplicitCreatorSignals = (context) => {
    if (!context || typeof context !== 'object') return false;

    return Boolean(
      String(context.handle || '').trim()
      || String(context.discount_code || '').trim()
      || String(context.discount_amount || '').trim()
      || String(context.template_type || '').trim()
      || parseProductList(context.most_recent_drop_products).length > 0
      || parseList(context.most_recent_drop_handles).length > 0
      || parseList(context.featured_handles).length > 0
    );
  };

  const normalizeContext = (context) => {
    const normalized = {
      handle: String(context.handle || '').trim(),
      first_name: String(context.first_name || '').trim(),
      last_name: String(context.last_name || '').trim(),
      shop_title: String(context.shop_title || '').trim(),
      profile_image: String(context.profile_image || '').trim(),
      bio: String(context.bio || '').trim(),
      slug: String(context.slug || '').trim(),
      discount_type: String(context.discount_type || '').trim(),
      discount_amount: String(context.discount_amount || '').trim(),
      discount_code: String(context.discount_code || '').trim(),
      template_type: String(context.template_type || '').trim(),
      default_build_box_size: String(context.default_build_box_size || '').trim(),
      most_recent_drop_products: parseProductList(context.most_recent_drop_products),
      most_recent_drop_handles: parseList(context.most_recent_drop_handles),
      featured_handles: parseList(context.featured_handles)
    };

    if (!normalized.slug && normalized.handle) {
      normalized.slug = normalized.handle;
    }

    if (!normalized.discount_amount && context.discount_amount !== undefined && context.discount_amount !== null) {
      normalized.discount_amount = String(context.discount_amount);
    }

    return normalized;
  };

  const mergeContexts = (contexts) => {
    const merged = {};
    contexts.forEach((context) => {
      if (!context || typeof context !== 'object') return;

      Object.entries(context).forEach(([key, value]) => {
        if (key === 'most_recent_drop_products') {
          const parsedProducts = parseProductList(value);
          if (parsedProducts.length > 0) {
            merged[key] = parsedProducts;
          }
          return;
        }

        if (Array.isArray(value)) {
          const parsed = parseList(value);
          if (parsed.length > 0) {
            merged[key] = parsed;
          }
          return;
        }

        if (value !== undefined && value !== null && String(value).trim() !== '') {
          merged[key] = value;
        }
      });
    });

    return normalizeContext(merged);
  };

  const persistContext = (context) => {
    if (!context.handle) return;
    window.localStorage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify(context));
  };

  const getPrebuildHandles = (context) => {
    if (context.most_recent_drop_products.length > 0) {
      const productHandles = context.most_recent_drop_products
        .map((product) => String(product.handle || '').trim())
        .filter(Boolean);

      if (productHandles.length > 0) return productHandles;
    }

    if (context.most_recent_drop_handles.length > 0) return context.most_recent_drop_handles;
    if (context.featured_handles.length > 0) return context.featured_handles;
    return [];
  };

  const syncCartAttributes = async (context) => {
    if (!context.handle) return;

    const cartAttributes = {
      'cc-creator-handle': context.handle
    };

    if (context.discount_code) {
      cartAttributes['cc-creator-discount-code'] = context.discount_code;
    }

    if (context.discount_amount) {
      cartAttributes['cc-creator-discount-percentage'] = context.discount_amount;
    }

    if (context.template_type) {
      cartAttributes['cc-creator-template-type'] = context.template_type;
    }

    const syncHash = JSON.stringify(cartAttributes);
    if (window.sessionStorage.getItem(CART_SYNC_HASH_KEY) === syncHash) return;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), CART_SYNC_TIMEOUT_MS);

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: cartAttributes
        }),
        signal: controller.signal
      });

      window.sessionStorage.setItem(CART_SYNC_HASH_KEY, syncHash);
    } catch (error) {
      // Failing to persist creator cart context should not break storefront UX.
      console.warn('CC creator context cart sync failed', error);
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const updateBuildABBoxLinks = (context) => {
    if (!context.handle) return;

    const buildABBoxLinks = Array.from(document.querySelectorAll('a[href*="/pages/build-a-box"], a[data-cc-build-a-box-link]'));
    if (buildABBoxLinks.length === 0) return;

    const prebuildHandles = getPrebuildHandles(context);
    const defaultSize = parsePositiveInteger(context.default_build_box_size);

    buildABBoxLinks.forEach((link) => {
      const href = link.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('mailto:')) return;

      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch (error) {
        return;
      }

      if (!url.pathname.includes('/pages/build-a-box')) return;

      url.searchParams.set('cc_prebuild', '1');

      if (!url.searchParams.get('size') && defaultSize) {
        url.searchParams.set('size', String(defaultSize));
      }

      if (context.handle) {
        url.searchParams.set('cc_handle', context.handle);
      }

      if (context.discount_code) {
        url.searchParams.set('cc_discount_code', context.discount_code);
      }

      if (context.discount_amount) {
        url.searchParams.set('cc_discount_pct', context.discount_amount);
      }

      if (context.template_type) {
        url.searchParams.set('cc_template_type', context.template_type);
      }

      if (!url.searchParams.get('cc_drop_handles') && prebuildHandles.length > 0) {
        url.searchParams.set('cc_drop_handles', prebuildHandles.join(','));
      }

      const nextHref = `${url.pathname}${url.search}${url.hash}`;
      link.setAttribute('href', nextHref);
    });
  };

  const updateNavAvatar = (context) => {
    const lockupTargets = Array.from(document.querySelectorAll('[data-cc-brand-lockup]'));
    if (lockupTargets.length === 0) return;

    const creatorName = [context.first_name, context.last_name].filter(Boolean).join(' ').trim()
      || context.shop_title
      || context.handle;

    lockupTargets.forEach((target) => {
      const avatar = target.querySelector('[data-cc-nav-avatar]');
      const image = target.querySelector('[data-cc-nav-avatar-image]');

      if (context.profile_image && context.handle && image) {
        image.src = context.profile_image;
        image.alt = creatorName ? `${creatorName} profile` : 'Creator profile';
        image.removeAttribute('hidden');
        avatar?.removeAttribute('hidden');
        target.removeAttribute('hidden');
      } else {
        image?.setAttribute('hidden', 'hidden');
        avatar?.setAttribute('hidden', 'hidden');
        target.setAttribute('hidden', 'hidden');
      }
    });
  };

  const applyCreatorContext = () => {
    const storedContext = readStoredContext();
    const queryContext = readQueryContext();
    const metaobjectContext = readMetaobjectContext();
    const creatorContextIsActive = hasExplicitCreatorSignals(queryContext) || hasExplicitCreatorSignals(metaobjectContext);

    if (!creatorContextIsActive) {
      updateNavAvatar(normalizeContext({}));
      return;
    }

    const context = mergeContexts([storedContext, queryContext, metaobjectContext]);

    if (!context.handle) {
      updateNavAvatar(normalizeContext({}));
      return;
    }

    persistContext(context);
    updateBuildABBoxLinks(context);
    updateNavAvatar(context);
    syncCartAttributes(context);
  };

  const initialize = () => {
    applyCreatorContext();

    window.addEventListener('cc:creator-context-ready', () => {
      applyCreatorContext();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();
